import { use, useMemo } from 'react';
import { useAvMutation, useGqlMutation, useGqlQuery } from '@API/useAvQuery';
import { FeatureFlags, useStaticErrorSnackBar, WorkflowType } from '@fabric-ui/components';
import { flattenObject, generateOption, QueryKey, uniqBy } from '@fabric-ui/utils';
import { UNKNOWN_ERROR_MESSAGE } from '@hooks/useHandleError';
import useQuerySql from '@hooks/useQuerySql';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch } from '@utils/AvRouter';
import { getGraphQLError } from '@utils/graphqlUtils';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../context/AvContextProvider';
import { NotificationContext } from '../../context/AvSnackBarProvider';
import { useBuildWhereClause } from '../Tickets/hooks';
import { FieldMapping, MappingTreeNode, MappingValidationProps } from './Mapping/types';
import { generateNestedSqlAccessor, getMappingMetadata, processMappings } from './Mapping/utils';
import { FileParser, MappingAgentDetails } from './types';

export const useFileReaderTypes = () => ({
  data: [
    { reader: null, title: 'Auto' },
    { reader: 'JSONL', title: 'JSONL' },
    { reader: 'JSON', title: 'JSON', fileReaderConfig: { rootJsonPath: '$' } },
    { reader: 'CSV', title: 'CSV', fileReaderConfig: { delimiter: ',', quote: '"', escape: '\\' } },
    { reader: 'XLSX', title: 'Excel', fileReaderConfig: { sheetNamePattern: '.*', useFirstSheet: false } },
    { reader: 'XML', title: 'XML', fileReaderConfig: { rootXPath: '' } },
    { reader: 'PYTHON_STREAM', title: 'PYTHON_STREAM', fileReaderConfig: { script: 'def' } },
    { reader: 'PARQUET', title: 'Parquet' },
  ],
});

const validateFileParser = (fileParser: FileParser, extension: string): boolean => {
  if (extension === 'xml' && fileParser?.reader === 'XML') {
    return !!fileParser.fileReaderConfig?.rootXPath;
  }

  return extension !== 'xml';
};

// TODO: Convert hook to useGqlMutation
export const useProcessNow = (onSuccess = d => d, muteErrors?, syncMode?) => {
  const { api } = useAvContext();
  return useMutation({
    mutationFn: ({ id, limitRows, ...body }: any) =>
      api(
        `data-source-instance/process?dsiId=${id}${limitRows ? `&limitRows=${limitRows}` : ''}${syncMode ? `&syncMode=${syncMode}` : ''}`,
        {
          options: { method: 'PUT' },
          body,
          muteErrors,
          onSuccess,
        }
      ),
  });
};

export const useDataPreview = ({ previewId, type, fileParser, disabled = false }) =>
  useGqlQuery<string[]>({
    queryKey: [QueryKey.DataPreview, previewId, fileParser],
    query: GET_PREVIEW,
    variables: {
      details: {
        fileId: previewId,
        dataSourceType: type.toUpperCase(),
        reader: fileParser?.reader,
        fileReaderConfig: fileParser?.fileReaderConfig,
      },
    },
    enabled: !!previewId && !!type && !disabled && validateFileParser(fileParser, previewId.split('.').at(-1)),
  });

export function useMapping(type, previewId, instanceId, onSuccess = d => d) {
  return useGqlQuery<any>({
    queryKey: [QueryKey.Mapping, type, previewId, instanceId],
    query: previewId ? GET_MAPPINGS_BY_TYPE : GET_MAPPINGS,
    variables: previewId ? { dataSourceType: type.toUpperCase() } : { id: instanceId },
    select: data => {
      const { fieldMapping } = (previewId ? data : data.mappingMetadata) ?? {};
      return {
        ...data,
        originalMappings: processMappings(fieldMapping ?? []),
      };
    },
    onSuccess,
    enabled: !!type,
    gcTime: 0,
  });
}

export const useDefaultMappings = (dsiType: string | undefined, onSuccess = d => d) =>
  useGqlQuery<{ fieldMapping: FieldMapping[] }, FieldMapping[]>({
    queryKey: [QueryKey.DefaultMapping, dsiType],
    query: GET_MAPPINGS_BY_TYPE,
    variables: { dataSourceType: dsiType?.toUpperCase() },
    select: ({ fieldMapping }) => processMappings(fieldMapping),
    onSuccess: data => {
      onSuccess(data);
    },
    enabled: !!dsiType,
  });

export const useSourceMappingId = (instanceId: string | undefined) =>
  useGqlQuery<{ id: string }, { id: string }>({
    queryKey: [QueryKey.SourceMappingId, instanceId],
    query: GET_SOURCE_MAPPING_ID,
    variables: { id: instanceId },
    enabled: !!instanceId,
  });

export const useMappingPreview = ({ previewId, type, instanceId, mapping, ...otherProps }) => {
  const query = useGqlQuery<any>({
    queryKey: [QueryKey.mappingPreview],
    query: GET_MAPPING_PREVIEW,
    variables: {
      details: {
        fileId: previewId,
        dsiId: instanceId,
        dataSourceType: type.toUpperCase(),
        mappingMetadata: getMappingMetadata(mapping),
        ...otherProps,
      },
    },
    enabled: !!type,
    gcTime: 0,
  });

  const data = useMemo(
    () =>
      query.data
        ? {
            rows: getRowsFromPreviewData(query.data.previewData),
            fieldErrors: getFieldErrorsMap(query.data),
            populatedFieldsList: query.data.populatedFieldsList || [],
          }
        : undefined,
    [query.data]
  );

  return { ...query, data };
};

// TODO: Convert hook to useGqlQuery
export const useBridgeHiddenFields = () => {
  const { api } = useAvContext();
  const {
    data = { bridge: {} },
    isPending: isLoading,
    error,
  } = useQuery({
    queryKey: [QueryKey.Bridge],
    queryFn: ({ signal }: { signal?: AbortSignal }) => api(GET_BRIDGE_QUERY, { signal, onSuccess: ({ data }) => getHiddenFields(data) }),
  });

  return { data, isLoading, error };
};

const getHiddenFields = data =>
  !data?.allBridgeDefinitions
    ? {}
    : data.allBridgeDefinitions.reduce((acc, entity) => {
        if (entity.entityTypeId?.name && entity.compositeKeyDefinition?.keyFieldName) {
          acc[entity.entityTypeId.name] = entity.compositeKeyDefinition.keyFieldName;
        }
        return acc;
      }, {});

export const getFieldErrorsMap = data =>
  data.metadata.errorsList.reduce(
    (map, { entityTypeId: { name }, fieldErrorsList }) => ({
      ...map,
      ...fieldErrorsList.reduce(
        (fieldMap, { fieldName, errorMessage }) => ({ ...fieldMap, [`${name.toLowerCase()}.${fieldName}`]: errorMessage }),
        {}
      ),
    }),
    {}
  );

export const useValidateMapping = () => {
  const { enqueueSnackbar } = useSnackbar();
  const staticErrorSnackBar = useStaticErrorSnackBar();
  return useGqlMutation<any, MappingValidationProps>({
    mutation: GET_MAPPING_VALIDATE,
    variablesAdapter: ({ previewId, type, instanceId, mapping, ...otherProps }: MappingValidationProps) => ({
      details: {
        fileId: previewId,
        dataSourceType: type?.toUpperCase(),
        dsiId: instanceId,
        mappingMetadata: getMappingMetadata([mapping]),
        ...otherProps,
      },
    }),
    onSuccess: (data, req) => {
      const errorsMap = getFieldErrorsMap(data);
      const errorMsg = errorsMap[`${req.mapping.entityName.toLowerCase()}.${req.mapping.modelFieldName}`];
      if (errorMsg) {
        staticErrorSnackBar({ title: 'Validation Failed', message: errorMsg });
      } else {
        enqueueSnackbar('Validation passed successfully!', { variant: 'success' });
      }
    },
    onError: error => {
      const errorMsg = error?.message;
      if (errorMsg) {
        staticErrorSnackBar({ title: 'Validation Failed', message: errorMsg });
      } else {
        enqueueSnackbar(UNKNOWN_ERROR_MESSAGE, { variant: 'error' });
      }
    },
  });
};

// TODO: Convert hook to useGqlQuery
export const useDSIData = ({ instanceId: id }) => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  return useQuery({
    queryKey: [QueryKey.FileParser, id],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(GET_DSI_DATA, {
        options: { id },
        signal,
        onError: () => enqueueSnackbar('Failed to fetch File Parser', { variant: 'error' }),
        onSuccess: ({ data }) => ({
          ...(data?.findDataSourceInstanceById || {}),
          fileParser: data?.findDataSourceInstanceById?.fileParser || undefined,
        }),
      }),
    enabled: !!id,
  });
};

// TODO: Convert hook to useGqlMutation
export const useDataSourceBulkUpdate = (onSuccess: () => void) => {
  const client = useQueryClient();
  const { api } = useAvContext();
  const { mutate } = useMutation({
    mutationFn: ({ dsiIds, active }: { dsiIds: string[]; active?: boolean }) =>
      api(BULK_UPDATE_DSIS, {
        options: { updateData: dsiIds.map(id => ({ id, active })) },
        onSuccess,
      }),
    onSuccess: () => client.refetchQueries({ queryKey: [QueryKey.GETSOURCESLIST] }),
  });
  return mutate;
};

// TODO: Convert hook to useGqlMutation
export const useSetFileParser = ({ instanceId: dsiId }) => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (fileParser: any) =>
      api(SET_FILE_PARSER, { options: { dsiId, ...fileParser } }).catch(e => {
        console.error(e);
        const userFacingError = 'Failed to save File Parser';
        enqueueSnackbar(userFacingError, { variant: 'error' });
        throw new Error(userFacingError);
      }),
  });
};

export const useDataAndFieldsPreview = ({ previewId, type, fileParser }) =>
  useGqlQuery<{ getFilePreview: string[]; getFileFieldsPreview: MappingTreeNode[] }, { data: string[]; fields: MappingTreeNode[] }>({
    queryKey: [QueryKey.DataPreviewFromWizard, previewId, fileParser],
    query: GET_PREVIEW_AND_FIELDS,
    variables: {
      details: {
        fileId: previewId,
        dataSourceType: type?.toUpperCase(),
        reader: fileParser?.reader,
        fileReaderConfig: fileParser?.fileReaderConfig,
      },
    },
    select: (_, { getFilePreview, getFileFieldsPreview }) => ({ data: getFilePreview, fields: getFileFieldsPreview }),
    enabled: !!previewId && !!type && validateFileParser(fileParser, previewId.split('.').at(-1)),
  });

// TODO: Convert hook to useGqlQuery
export const useDataStreamInstanceAndFieldsPreview = (
  {
    instanceId,
    fileParser,
    onSuccess = d => d,
    onError,
  }: { instanceId: string | undefined; fileParser; onSuccess?: (any) => any; onError?: (e) => void },
  enabled
) => {
  const { api } = useAvContext();
  return useQuery({
    queryKey: [QueryKey.UseDataStreamInstancePreviewSource, instanceId, fileParser, onSuccess.toString()],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(GET_INSTANCE_PREVIEW_SOURCE, {
        signal,
        options: { instanceId, reader: fileParser?.reader, fileReaderConfig: fileParser?.fileReaderConfig },
        onSuccess: data => ({ data: data?.data?.getDataSourceInstanceFilePreview, fields: data?.data?.getDataSourceInstanceFileFields }),
        onError,
      }),
    enabled: !!enabled,
    gcTime: 0,
  });
};

// TODO: Convert hook to useGqlQuery
export const useDataStreamInstancePreview = (
  {
    instanceId,
    fileParser,
    onSuccess = d => d,
    onError,
  }: { instanceId: string | undefined; fileParser; onSuccess?: (any) => any; onError?: (e) => void },
  enabled
) => {
  const { api } = useAvContext();
  return useQuery({
    queryKey: [QueryKey.UseDataStreamInstancePreview, instanceId, fileParser, onSuccess.toString()],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(GET_INSTANCE_PREVIEW, {
        signal,
        options: { instanceId, reader: fileParser?.reader, fileReaderConfig: fileParser?.fileReaderConfig },
        onSuccess: ({ data }) => onSuccess(data?.getDataSourceInstanceFilePreview),
        onError,
      }),
    enabled: !!instanceId && !!enabled,
    gcTime: 0,
  });
};

export const useGetMappingPreviewFilterSQL = () => {
  const filters = useSearch({});
  const transformedFilters = Object.entries(filters)
    .filter(([key]) => key !== 'orderBy')
    .reduce((acc, [key, value]) => ({ ...acc, [generateNestedSqlAccessor(key)]: value }), {});
  return useBuildWhereClause({ filters: transformedFilters, allowAllFields: true }).replace('where', '');
};

export const useMappingStatsData = ({ workflowId, col }: { workflowId: string | undefined; col: string }) => {
  const { id: instanceId } = useParams();
  const colExpression = useMemo(() => generateNestedSqlAccessor(col), [col]);

  const sourceFilter = instanceId ? `rawlogs.dsi_id = '${instanceId}'` : `(rawlogs.dsi_id = '' OR rawlogs.dsi_id = null)`;

  const where = useGetMappingPreviewFilterSQL();
  return useQuerySql({
    key: 'mapping-stats',
    sql: `
        SELECT
            COUNT_IF(1, ${colExpression} = null) as nullCount,
            COUNT_IF(1, ${colExpression} = '') as emptyCount,
            COUNT_IF(1, ${colExpression} != null AND ${colExpression} != '') as populatedCount,
            COUNT_DISTINCT(${colExpression}) as distinctCount
        FROM rawlogs
        WHERE AVALOR_CONTEXT(rawlogs, 'workflowId') = '${workflowId}' AND ${sourceFilter}
            ${where ? `AND ${where}` : ''}
    `,
    options: {
      enabled: !!workflowId && !!col,
      placeholderData: keepPreviousData,
    },
  });
};

export const useGetMappingStatsSQL = ({
  topValues,
  col,
  workflowId,
}: {
  topValues: number;
  col: string;
  workflowId: string | undefined;
}) => {
  const { id: instanceId } = useParams();
  const sourceFilter = instanceId ? `rawlogs.dsi_id = '${instanceId}'` : `(rawlogs.dsi_id = '' OR rawlogs.dsi_id = null)`;
  const colExpression = useMemo(() => generateNestedSqlAccessor(col), [col]);
  const where = useGetMappingPreviewFilterSQL();

  return `
  SELECT 
      ${colExpression} as val, 
      COUNT(rawlogs.DOCUMENT) as count 
          FROM rawlogs 
      WHERE AVALOR_CONTEXT(rawlogs, 'workflowId') = '${workflowId}' AND ${sourceFilter}
      ${where ? `AND ${where}` : ''}
      GROUP BY val
      ORDER BY count DESC 
      LIMIT ${topValues}
  `;
};

export const useGetSuppressionFields = (fileId, type, newData, id, isLoading, onError, disabled = false) => {
  const { data: dataPreview, isLoading: isLoadingPreview } = useDataPreview({
    previewId: fileId,
    type,
    fileParser: newData.fileParser,
    disabled,
  });

  const { fields: suppressionFieldsFilePreview } = useTransformDataPreview(dataPreview);

  const { data: suppressionFieldsDSI, isLoading: isLoadingDSI } = useDataStreamInstancePreview(
    {
      instanceId: id,
      fileParser: newData.fileParser,
      onSuccess: rows => (rows?.[0] ? Object.keys(JSON.parse(rows[0])).map(generateOption) : []),
      onError,
    },
    !isLoading && newData
  );

  return {
    suppressionFields: suppressionFieldsDSI || suppressionFieldsFilePreview,
    isLoading: isLoadingPreview || isLoadingDSI,
  };
};

export const useSuggestedMapping = (details, onSuccess = d => d) =>
  useGqlQuery({
    queryKey: [QueryKey.SuggestMapping],
    query: SUGGEST_MAPPING,
    variables: { details },
    onSuccess,
    enabled: false,
  });

export const useMappingAgent = (onSuccess = d => d, onError?: () => void) => {
  const { setRunInfo } = use(NotificationContext);

  return useGqlMutation<{ workflowRun: { id: string; runId: string } }, { details: MappingAgentDetails }>({
    mutationKey: [QueryKey.MappingAgent],
    mutation: SUGGEST_MAPPING_AGENT,
    onSuccess: ({ workflowRun: { id, runId } }) => {
      if (id && runId) {
        setRunInfo({
          wfId: id,
          runId,
          workflowType: WorkflowType.AutoMap,
          returnStatusOnly: false,
          progressPercentage: 0,
          shouldBlock: true,
          onSuccess: data => {
            if (data.status === 'FAILED') {
              onError?.();
              return;
            }
            // mappings are a json inside the first result
            const firstResult = data.result?.[0];
            if (firstResult) {
              onSuccess(JSON.parse(firstResult).mappings);
            }
          },
        });
      }
    },
  });
};

export const useAddEntity = (onSuccess: () => void) => {
  const { enqueueSnackbar } = useSnackbar();
  return useGqlMutation<{ id: { name: string; builtIn: boolean } }, { name: string }>({
    mutation: ADD_ENTITY_DW2,
    variablesAdapter: ({ name }: { name: string }) => ({
      req: { id: { displayName: name, name: name.replaceAll(' ', '_').toLowerCase(), builtIn: false } },
    }),
    onSuccess: () => {
      enqueueSnackbar('Entity created successfully', { variant: 'success' });
      onSuccess?.();
    },
  });
};

// TODO: Convert hook to useGqlMutation
export const useAddFieldToEntity = (onSuccess = d => d) => {
  const { api } = useAvContext();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<() => Promise<any>, unknown, { displayName; name; fieldType; entityTypeId; repeated; groupName }>({
    mutationFn: ({ displayName, name, fieldType, entityTypeId, repeated = false, groupName }) =>
      api(CREATE_ENTITY_FIELD, {
        options: {
          request: {
            typeId: entityTypeId,
            fieldType,
            repeated,
            displayName: displayName || name,
            name,
            groupName,
          },
        },
        muteErrors: true,
        onSuccess: data => {
          enqueueSnackbar(`Field created successfully`, { variant: 'success' });
          queryClient.refetchQueries({ queryKey: [QueryKey.Entities] });
          return onSuccess(data);
        },
      }).catch(error => {
        if ([error?.status, getGraphQLError(error)?.extensions?.status].includes(400)) {
          console.warn(error);
        } else {
          console.error(error);
        }
        enqueueSnackbar(error?.message || `Failed to create field ${name} for entity ${entityTypeId.name}`, {
          variant: 'error',
        });
      }),
  });
};

// TODO: Convert hook to useGqlMutation
export const useUpdateFieldToEntity = () => {
  const { api } = useAvContext();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<() => Promise<any>, unknown, { fields }>({
    mutationFn: ({ fields }) =>
      api(BULK_UPDATE_ENTITY_FIELDS, {
        options: {
          request: {
            fields: fields.map(({ entityTypeId, displayName, name }) => ({
              typeId: entityTypeId,
              name,
              displayName,
            })),
          },
        },
        onSuccess: ({ data: { bulkUpdateEntityFields: data } }) => {
          if (data.failedFields.length > 0) {
            enqueueSnackbar(data.failedFields.map(e => e.message).join(', '), { variant: 'error' });
            console.warn(data.failedFields);
          } else {
            enqueueSnackbar(`Fields updated successfully`, { variant: 'success' });
          }
          queryClient.refetchQueries({ queryKey: [QueryKey.Entities] });
          return data;
        },
      }),
  });
};

export const getAllFieldDefs = (mapping, row) =>
  uniqBy(
    [
      ...mapping.map(item => ({
        ...item,
        fieldKey: `${item.entityName.toLowerCase()}.${item.modelFieldName}`,
      })),
      ...Object.keys(row)
        .filter(f => f !== 'avalorId')
        .map(fieldKey => ({
          fieldKey,
          entityName: fieldKey.split('.')[0].toLowerCase(),
          modelFieldName: fieldKey.split('.')[1],
          isAutoMapped: true,
        })),
    ],
    ({ fieldKey }) => fieldKey
  ).sort((a, b) => a.fieldKey.localeCompare(b.fieldKey));

const addEntityPrefix = (fields, entityName) =>
  Object.keys(fields).reduce(
    (obj, key) => ({
      ...obj,
      [`${entityName}.${key}`]: fields[key],
    }),
    {}
  );
export const getRowsFromPreviewData = (data, getFieldPrefix = entityName => entityName.toLowerCase()) =>
  data.map(({ avalor_id: avalorId, flat_model: fm, aggregationValues, raw_data, ...generalFields }) =>
    (fm || []).reduce(
      (row, entityModel) => {
        const { model, ...metadata } = entityModel.entity;
        const fields = { ...metadata, ...model };
        const entityName = getFieldPrefix(entityModel.type_id.name);
        return {
          ...row,
          ...addEntityPrefix(flattenObject({ obj: fields, shouldFlattenArray: false }), entityName),
          // eslint-disable-next-line no-underscore-dangle
          avalorId: avalorId || fields._key,
        };
      },
      {
        ...addEntityPrefix(flattenObject({ obj: generalFields, shouldFlattenArray: false }), 'general'),
        ...(aggregationValues && { agg_values: aggregationValues }),
      }
    )
  );

const sortedHeaders = ['severity', 'id'].reverse();

export function useTransformDataPreview(data) {
  const rows = data?.map((row, index) => ({ ...JSON.parse(row), avalor_unique_key: index })) || [];
  const headCells =
    !rows || !rows[0]
      ? []
      : Object.keys(rows[0])
          .sort((a, b) => sortedHeaders.indexOf(b) - sortedHeaders.indexOf(a))
          .map(key => ({
            id: key,
            label: key,
            isKey: key === 'avalor_unique_key',
            hidden: key === 'avalor_unique_key',
            style: { maxWidth: 250 },
            getValue: ({ [key]: val }: any) => (typeof val === 'object' ? JSON.stringify(val) : val?.toString()),
          }));
  return {
    rows,
    headCells,
    fields: headCells
      .filter(({ id }) => id !== 'avalor_unique_key')
      .map(({ id, label }) => ({
        value: id,
        title: label,
      })),
  };
}

export const useTestConnection = ({
  newData,
  configFields,
  showOneAuthUi,
  isEditMode,
  type,
  handleError,
}: {
  newData: any;
  configFields: any;
  showOneAuthUi: boolean;
  isEditMode: boolean;
  type: string;
  handleError?: (errors) => void;
}) => {
  const { api, featureFlags } = useAvContext();

  return useAvMutation({
    mutationFn: ({ onSuccess, onError }: { onSuccess: () => void; onError: () => void }) =>
      api(featureFlags[FeatureFlags.UseNewErrorsFramework] ? VALIDATE_DATA_RETRIEVAL_NEW : VALIDATE_DATA_RETRIEVAL, {
        options: {
          config: {
            ...configFields.reduce(
              (acc, { valueName, hideIfOneAuth, defaultValue }) => ({
                ...acc,
                [valueName]:
                  hideIfOneAuth && showOneAuthUi && defaultValue !== null && newData.retrievalConfig[valueName] === undefined
                    ? defaultValue
                    : newData.retrievalConfig[valueName],
              }),
              {}
            ),
            ...(newData.retrievalConfig.avalorGatewayDnsName && {
              avalorGatewayDnsName: newData.retrievalConfig.avalorGatewayDnsName,
            }),
            deliveryMethod: newData.deliveryMethod,
          },

          deliveryMethod: newData.deliveryMethod,
          id: isEditMode ? newData.id : '',
          authenticationSourceId: newData.authenticationSourceId ? newData.authenticationSourceId : '',
          ...(featureFlags[FeatureFlags.UseNewErrorsFramework] ? { dataSourceType: type } : {}),
        },
        muteErrors: true,
        onSuccess,
      }).catch(handleError || onError),
  });
};

// TODO: Convert hook to useGqlMutation
export const useCancelWorkflow = guidRuns => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ onSuccess }: { onSuccess: () => void }) =>
      api(CANCEL_WORKFLOW, {
        options: { wfExecutions: { workflowsExecutions: guidRuns, dsiId: null } },
        onError: () => enqueueSnackbar('Failed to cancel workflow ', { variant: 'error' }),
        onSuccess: () => {
          enqueueSnackbar('Workflow Execution canceled successfully ', { variant: 'success' });
          onSuccess();
        },
      }),
  });
};

const GET_PREVIEW = gql`
  query getFilePreview($details: DataSourcePreviewDetails!) {
    getFilePreview(details: $details)
  }
`;

const GET_PREVIEW_AND_FIELDS = gql`
  query getFilePreview($details: DataSourcePreviewDetails!) {
    getFilePreview(details: $details)
    getFileFieldsPreview(details: $details)
  }
`;

const GET_INSTANCE_PREVIEW_SOURCE = gql`
  query getDataSourceInstanceFilePreview($instanceId: String!, $reader: FileReaderType, $fileReaderConfig: JsonNode) {
    getDataSourceInstanceFilePreview(dsiId: $instanceId, reader: $reader, fileReaderConfig: $fileReaderConfig)
    getDataSourceInstanceFileFields(dsiId: $instanceId, reader: $reader, fileReaderConfig: $fileReaderConfig)
  }
`;

const GET_DSI_DATA = gql`
  query findDataSourceInstanceById($id: String!) {
    findDataSourceInstanceById(id: $id) {
      dataSourceType
      name
      mappedSourceName
      iconUrl
      fileParser {
        id
        reader
        fileReaderConfig
      }
    }
  }
`;

export const BULK_UPDATE_DSIS = gql`
  mutation dataSourceBulkUpdate($updateData: [DataSourceUpdateData]!) {
    dataSourceBulkUpdate(dataSourceBulkUpdateData: $updateData)
  }
`;

const SET_FILE_PARSER = gql`
  mutation setDsiParser($dsiId: String!, $reader: FileReaderType, $fileReaderConfig: JsonNode) {
    setDsiParser(dsiId: $dsiId, reader: $reader, fileReaderConfig: $fileReaderConfig)
  }
`;

const GET_INSTANCE_PREVIEW = gql`
  query getDataSourceInstanceFilePreview($instanceId: String!, $reader: FileReaderType, $fileReaderConfig: JsonNode) {
    getDataSourceInstanceFilePreview(dsiId: $instanceId, reader: $reader, fileReaderConfig: $fileReaderConfig)
  }
`;

const GET_MAPPING_PREVIEW = gql`
  query getTransformationPreview($details: TransformationPreviewDetails!) {
    getTransformationPreview(details: $details) {
      previewData
      metadata {
        totalRows
        errorsList {
          entityTypeId {
            name
            builtIn
          }
          fieldErrorsList {
            fieldName
            errorMessage
          }
        }
        parsingErrorsList
      }
      populatedFieldsList
    }
  }
`;

const GET_BRIDGE_QUERY = gql`
  query allBridgeDefinitions {
    allBridgeDefinitions {
      entityTypeId {
        name
        builtIn
      }
      compositeKeyDefinition {
        keyFieldName
      }
    }
  }
`;

const GET_MAPPING_VALIDATE = gql`
  query getTransformationPreview($details: TransformationPreviewDetails!) {
    getTransformationPreview(details: $details) {
      metadata {
        errorsList {
          entityTypeId {
            name
            builtIn
          }
          fieldErrorsList {
            fieldName
            errorMessage
          }
        }
        parsingErrorsList
      }
    }
  }
`;

const CREATE_ENTITY_FIELD = gql`
  mutation createEntityField($request: AddEntityFieldRequest) {
    createEntityField(request: $request) {
      name
    }
  }
`;

const BULK_UPDATE_ENTITY_FIELDS = gql`
  mutation bulkUpdateEntityFields($request: BulkUpdateEntityFieldsRequest) {
    bulkUpdateEntityFields(request: $request) {
      updatedFields {
        name
      }
      failedFields {
        fieldName
        message
      }
    }
  }
`;

export const SUGGEST_MAPPING = gql`
  query suggestMapping($details: MappingSuggestionRequestDetails) {
    suggestMapping(details: $details)
  }
`;

export const SUGGEST_MAPPING_AGENT = gql`
  mutation triggerMappingAgentWorkflow($details: MappingAgentDetails!) {
    triggerMappingAgentWorkflow(details: $details) {
      workflowRun {
        id
        runId
      }
    }
  }
`;

export const ADD_ENTITY_DW2 = gql`
  mutation addEntityType($req: EntityTypeScalar!) {
    addEntityType(entityType: $req) {
      id {
        builtIn
        name
      }
    }
  }
`;

const GET_MAPPINGS = gql`
  query findMappingByDataSourceInstanceId($id: String!) {
    findMappingByDataSourceInstanceId(id: $id) {
      id
      name
      dataSourceType
      mappingMetadata
    }
  }
`;
const GET_MAPPINGS_BY_TYPE = gql`
  query getDefaultMappings($dataSourceType: String!) {
    getDefaultMappings(dataSourceType: $dataSourceType)
  }
`;

const GET_SOURCE_MAPPING_ID = gql`
  query findMappingByDataSourceInstanceId($id: String!) {
    findMappingByDataSourceInstanceId(id: $id) {
      id
    }
  }
`;

const VALIDATE_DATA_RETRIEVAL = gql`
  mutation validateDataRetrieval(
    $config: IDataSourceInstanceConfig!
    $deliveryMethod: String!
    $id: String!
    $authenticationSourceId: String!
  ) {
    validateDataRetrieval(config: $config, deliveryMethod: $deliveryMethod, id: $id, authenticationSourceId: $authenticationSourceId)
  }
`;

const VALIDATE_DATA_RETRIEVAL_NEW = gql`
  mutation validateDataRetrievalNew(
    $config: IDataSourceInstanceConfig!
    $deliveryMethod: String!
    $id: String!
    $authenticationSourceId: String!
    $dataSourceType: String!
  ) {
    validateDataRetrievalNew(
      config: $config
      deliveryMethod: $deliveryMethod
      id: $id
      authenticationSourceId: $authenticationSourceId
      dataSourceType: $dataSourceType
    )
  }
`;

const CANCEL_WORKFLOW = gql`
  mutation cancelWorkflows($wfExecutions: workflowExecutionInput!) {
    cancelWorkflows(wfExecutions: $wfExecutions)
  }
`;
